import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import useSearchQueryParams from 'hooks/use-search-params';
import useStorefrontSettings from 'hooks/useStorefrontSettings';

/** `?storefront=true|false` in the embed URL; remembered for the tab so it survives navigation. */
const OVERRIDE_KEY = 'storefront';

const readOverride = (param: unknown): boolean | undefined => {
  const value = Array.isArray(param) ? param[0] : param;
  if (value === 'true' || value === 'false') {
    try {
      sessionStorage.setItem(OVERRIDE_KEY, value);
    } catch {
      // Without sessionStorage the override lasts as long as the param does.
    }
    return value === 'true';
  }
  try {
    const stored = sessionStorage.getItem(OVERRIDE_KEY);
    if (stored === 'true' || stored === 'false') return stored === 'true';
  } catch {
    // ignore
  }
  return undefined;
};

export interface StorefrontHome {
  /** True when `/` and `/search` render the root catalog instead of the homepage. */
  readonly isEnabled: boolean;
  readonly rootCatalogId: string | null;
}

/**
 * The go-live switch: when the brand's `storefront` feature is on and it has a root catalog, the
 * embed's home is that catalog, at the same URLs guests use today. `?storefront=true` turns it on
 * for this tab regardless of the flag and `?storefront=false` turns it off, the way `?v2=true`
 * trialled the landing page; either sticks in sessionStorage so a flow can be walked without
 * re-adding the param. A brand with the flag but no root catalog keeps the homepage.
 */
const useStorefrontHome = (): StorefrontHome => {
  const flag: boolean = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.STOREFRONT,
  );
  const { searchParams } = useSearchQueryParams();
  const { rootCatalogId } = useStorefrontSettings();
  const override = readOverride(searchParams?.[OVERRIDE_KEY]);
  const wanted = override ?? flag;
  const isEnabled = wanted && Boolean(rootCatalogId);
  return { isEnabled, rootCatalogId: isEnabled ? rootCatalogId : null };
};

export default useStorefrontHome;
